import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { exportToExcel } from "../../utils/exportExcel";
import { Button } from "../../components/ui/button";
import { Clock, Users, TrendingUp, Calendar, Download } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const fmtBreaks = (breaks = []) => {
  const total = breaks
    .filter((b) => b.start && b.end)
    .reduce((acc, b) => acc + (new Date(b.end) - new Date(b.start)) / 60000, 0);
  if (!total) return "—";
  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function AttendanceDashboard() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split("T")[0]);

  const fetchData = (d) => {
    setLoading(true);
    api.get(`/attendance${d ? `?date=${d}` : ""}`).then((r) => setRecords(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(dateFilter); }, []);

  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
    fetchData(e.target.value);
  };

  const totalPresent = records.filter((r) => r.clock_in).length;
  const avgHours = records.length > 0
    ? (records.reduce((a, r) => a + (r.active_hours || 0), 0) / records.filter((r) => r.clock_out).length || 0).toFixed(1)
    : 0;

  const handleExport = () => {
    if (records.length === 0) { toast.error("No records to export"); return; }
    const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
    const calcBreak = (breaks = []) =>
      Math.round(breaks.filter((b) => b.start && b.end)
        .reduce((a, b) => a + (new Date(b.end) - new Date(b.start)) / 60000, 0));
    const sheet = records.map((r) => ({
      Employee: r.employee_name, Date: r.date,
      "Clock In": fmtT(r.clock_in), "Clock Out": fmtT(r.clock_out),
      "Break (mins)": calcBreak(r.breaks), "Active Hours": r.active_hours || 0,
      Status: r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent",
    }));
    exportToExcel([{ data: sheet, name: "Attendance" }], `Attendance_${dateFilter || "All"}`);
    toast.success("Attendance exported!");
  };

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-7">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Time & Attendance</h1>
            <p className="text-slate-500 text-sm mt-1">Track employee clock-ins, clock-outs and work hours</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={handleDateChange}
                className="text-sm text-slate-700 outline-none bg-transparent"
                data-testid="attendance-date-filter"
              />
            </div>
            <Button
              onClick={handleExport}
              variant="outline"
              className="gap-2 border-slate-200 text-slate-700 hover:border-[#38b6e8] hover:text-[#38b6e8]"
              data-testid="export-attendance-btn"
            >
              <Download size={15} /> Export Excel
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Users, label: "Present", value: totalPresent, color: "text-[#38b6e8]", bg: "bg-sky-50" },
            { icon: TrendingUp, label: "Avg Hours", value: `${avgHours}h`, color: "text-emerald-600", bg: "bg-emerald-50" },
            { icon: Clock, label: "Clocked Out", value: records.filter((r) => r.clock_out).length, color: "text-violet-600", bg: "bg-violet-50" },
            { icon: Users, label: "On Break", value: records.filter((r) => r.breaks?.some((b) => !b.end)).length, color: "text-amber-600", bg: "bg-amber-50" },
          ].map(({ icon: Icon, label, value, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${bg}`}><Icon size={18} className={color} /></div>
              <div>
                <p className="text-slate-500 text-xs font-medium">{label}</p>
                <p className="text-xl font-bold text-[#1a2332]">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center">
              <Clock size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No attendance records for this date</p>
            </div>
          ) : (
            <table className="w-full" data-testid="attendance-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Employee", "Date", "Clock In", "Clock Out", "Break Time", "Active Hours", "Status"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`attendance-row-${r.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-800 text-sm">{r.employee_name}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-sm">{r.date}</td>
                    <td className="px-5 py-4">
                      <span className={`text-sm font-medium ${r.clock_in ? "text-emerald-600" : "text-slate-400"}`}>
                        {fmt(r.clock_in)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-sm font-medium ${r.clock_out ? "text-slate-700" : "text-amber-500"}`}>
                        {r.clock_out ? fmt(r.clock_out) : (r.clock_in ? "In progress" : "—")}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-sm">{fmtBreaks(r.breaks)}</td>
                    <td className="px-5 py-4">
                      <span className="font-semibold text-slate-800 text-sm">
                        {r.active_hours > 0 ? `${r.active_hours}h` : "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        r.clock_out ? "bg-emerald-100 text-emerald-700" :
                        r.clock_in ? "bg-sky-100 text-sky-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
